import { Oauth, Tools } from '@ezbobdev/ui-sdk';

/**
 * GRN-7569 — drop a stale customer session before the app makes its first API call.
 *
 * The problem, reproduced on UAT: entering the flow, then entering the same flow as the same
 * user in another browser (or profile), leaves the first browser holding a `user_token` cookie
 * whose jti the backend has since replaced. The cookie resolver returns the FIRST cookie it
 * finds, in the order user_token, case_token, access_token
 * (CookieAndHeaderBearerTokenResolver#resolve), so that dead customer token shadows a perfectly
 * valid app token and every call it authenticates is rejected — including
 * `GET /v1/configuration/external/Ui`, without which the app cannot finish booting.
 *
 * That is why this cannot live inside the React tree: the existing entry cleanup (GRN-6811) runs
 * in the Onboarding widget, which never mounts, because configuration never loads.
 *
 * Why a token mint rather than `clear-session`: the cookies are HttpOnly and scoped to the API
 * host, so script cannot delete them — only a `Set-Cookie` from the API can. `clear-session` is
 * not exempt from the CSRF check, and pre-boot we have no CSRF value to send (it is HttpOnly
 * too), so it would be rejected. The token endpoint IS exempt
 * (CookieAndHeaderBearerTokenResolver#isTokenExtractionBlocked) and a mint without
 * `preserveCookies=true` deletes `case_token` and `user_token` (TokenAndCookieFilter#removeOldCookies).
 *
 * Only entries carrying a `code` are affected — i.e. the external-login products, Morning and
 * Corporates. A plain reload with no `code` must keep its cookies, or a live session would be
 * wiped and forced into an unrecoverable logout (the GRN-6822 §4.3b case).
 */

const ENTRY_CODE_PARAM = 'code';
const CHANNEL_PARAM = 'channel';
const CHANNEL_STORAGE_KEY = 'channelId';

/**
 * Who the browser thinks it is. Expiring the cookies is only half the job: on boot UIFramework
 * rehydrates every sessionStorage key into `Tools.options`, so a surviving `jti` / `userData`
 * would describe a user whose token the backend has already replaced.
 *
 * Three deliberate exclusions:
 *
 * - `caseId`, `caseToken` and `journeyUuid` — WHICH case, not WHO. Clearing them sends
 *   `useOnboarding.setUser` down its create-a-case branch (useOnboarding.js:246), and
 *   service-cases refuses that with `AuthorizationDeniedException` on a renewal entry. The
 *   external login response carries `additionalInformation.caseUuid`/`journeyUuid` and
 *   `handleExternalLoginResults` (SdkActions.js:1748) overwrites these anyway, so a stale
 *   pointer is corrected a moment later rather than being a problem to pre-empt.
 * - `channelId` and `productPath` — which product is being entered, not who is entering it;
 *   `channelId` is also needed for the mint below.
 * - localStorage entirely: it holds `attributions` and idle-timer bookkeeping, and the in-app
 *   GRN-6811 cleanup's blanket `localStorage.clear()` destroys marketing attribution on every
 *   single entry. Ours does not.
 */
const SESSION_IDENTITY_KEYS = [
  'accessToken',
  'csrfToken',
  'expiresIn',
  'refreshToken',
  'scope',
  'userData',
  'userId',
  'jti',
];

// A hung request must not hold the app hostage; the app boots and the in-app cleanup remains
// as a second line of defence.
const MINT_TIMEOUT_MS = 8000;

/**
 * True when the current URL carries an external-login entry code.
 *
 * @returns {boolean}
 */
export const isExternalLoginEntry = () => {
  try {
    const code = new URLSearchParams(window.location.search).get(ENTRY_CODE_PARAM);

    return typeof code === 'string' && code.trim() !== '';
  } catch {
    return false;
  }
};

const resolveChannelId = () => {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get(CHANNEL_PARAM);

    if (fromUrl) {
      return fromUrl;
    }

    return sessionStorage.getItem(CHANNEL_STORAGE_KEY) || Tools.getOption(CHANNEL_STORAGE_KEY) || undefined;
  } catch {
    return undefined;
  }
};

/**
 * Removes the stored session identity, so the cookie state and the frontend's own state agree.
 *
 * @returns {string[]} the keys that were actually present and removed
 */
export const clearStoredSessionIdentity = () => {
  const removed = [];

  SESSION_IDENTITY_KEYS.forEach((key) => {
    try {
      if (sessionStorage.getItem(key) !== null) {
        sessionStorage.removeItem(key);
        removed.push(key);
      }
    } catch {
      // sessionStorage unavailable (private mode / quota) — nothing to clear.
    }
  });

  return removed;
};

const withTimeout = (promise, ms) =>
  Promise.race([
    promise,
    new Promise((resolve) => {
      setTimeout(() => resolve({ timedOut: true }), ms);
    }),
  ]);

/**
 * Clears the stored session identity and mints a fresh app token with `preserveCookies=false`
 * when the URL carries an entry code, so the backend expires the stale `user_token` and
 * `case_token` cookies and the frontend forgets the case they belonged to. Resolves to a short
 * status string for logging; never rejects, because failing to clean up must not stop the app from
 * starting.
 *
 * Must be awaited before the first API call of the app (i.e. before React renders).
 *
 * @returns {Promise<'skipped'|'cleared'|'timed-out'|'failed'>}
 */
export const clearStaleSessionOnEntry = async () => {
  if (!isExternalLoginEntry()) {
    return 'skipped';
  }

  // Order matters: read the channel first (below), then drop the identity, then mint. The mint
  // is what expires the cookies, so both halves of the session go before the app's first call.
  const channelId = resolveChannelId();

  clearStoredSessionIdentity();

  try {
    const result = await withTimeout(Oauth.getAppToken(channelId, false), MINT_TIMEOUT_MS);

    return result && result.timedOut ? 'timed-out' : 'cleared';
  } catch {
    return 'failed';
  }
};
